import { useState, useEffect } from "react";
import { Card } from "@app/components/shared/Card";
import { Button } from "@app/components/shared/Button";
import { Skeleton } from "@app/components/shared/Skeleton";
import { getTechnicalAnalysis, type TechnicalAnalysis } from "@app/services/tradingSystemApi";
import { logger } from "@app/services/logger";
import type { TradeRecommendation } from "@shared/types";

interface RecommendationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  recommendation: TradeRecommendation | null;
}

export function RecommendationDetailsModal({
  isOpen,
  onClose,
  recommendation,
}: RecommendationDetailsModalProps): JSX.Element | null {
  const [technicalAnalysis, setTechnicalAnalysis] = useState<TechnicalAnalysis | null>(null);
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);
  const [showTechnicalAnalysis, setShowTechnicalAnalysis] = useState(false);
  
  useEffect(() => {
    if (isOpen && recommendation && showTechnicalAnalysis) {
      loadTechnicalAnalysis();
    }
  }, [isOpen, recommendation, showTechnicalAnalysis]);
  
  async function loadTechnicalAnalysis(): Promise<void> {
    if (!recommendation) return;
    
    setLoadingAnalysis(true);
    try {
      const analysis = await getTechnicalAnalysis(recommendation.symbol, "1h,4h,1d");
      setTechnicalAnalysis(analysis);
    } catch (error) {
      logger.error("Error loading technical analysis", error instanceof Error ? error : new Error(String(error)));
    } finally {
      setLoadingAnalysis(false);
    }
  }
  
  if (!isOpen || !recommendation) {
    return null;
  }

  function formatDate(dateString: string | null | undefined): string {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) {
        return "N/A";
      }
      // Use fixed English format: YYYY-MM-DD HH:mm:ss (independent of browser/OS locale)
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      const seconds = String(date.getSeconds()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch {
      return "N/A";
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <Card
        className="modal-card"
        title={`Recommendation Details: ${recommendation.symbol}`}
        subtitle={`Source: ${recommendation.source === "news" ? "News" : "Platform"} | Status: ${recommendation.status}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ padding: "12px", minWidth: "600px", maxHeight: "75vh", overflowY: "auto" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div>
              <strong>Summary</strong>
              <p style={{ marginTop: "6px", color: "var(--color-text-secondary)" }}>
                {recommendation.summary}
              </p>
            </div>

            <div>
              <strong>Confidence</strong>
              <p style={{ marginTop: "6px" }}>
                {recommendation.confidence !== undefined && recommendation.confidence !== null
                  ? (recommendation.confidence * 100).toFixed(1)
                  : "N/A"}%
              </p>
            </div>


            <div>
              <strong>Entry Conditions</strong>
              <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "3px" }}>
                {recommendation.conditions && recommendation.conditions.length > 0 ? (
                  recommendation.conditions.map((condition, index) => (
                    <div
                      key={index}
                      style={{
                        padding: "8px",
                        borderRadius: "4px",
                        background: condition.is_met ? "var(--color-success-light)" : "var(--color-error-light)",
                        color: condition.is_met ? "var(--color-success)" : "var(--color-error)",
                      }}
                    >
                      {condition.is_met ? "✓" : "✗"} {condition.label}
                      {condition.last_checked_at && (
                        <span style={{ fontSize: "0.875rem", marginLeft: "8px", opacity: 0.7 }}>
                          (Checked: {formatDate(condition.last_checked_at)})
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <span style={{ color: "var(--color-text-secondary)" }}>No conditions specified</span>
                )}
              </div>
            </div>

            <div>
              <strong>Entry Price</strong>
              <p style={{ marginTop: "6px" }}>{recommendation.entry}</p>
            </div>

            <div>
              <strong>Stop Loss</strong>
              <p style={{ marginTop: "6px" }}>{recommendation.stop_loss}</p>
            </div>

            <div>
              <strong>Take Profit Targets</strong>
              <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "3px" }}>
                {recommendation.take_profit && recommendation.take_profit.length > 0 ? (
                  recommendation.take_profit.map((tp, index) => (
                    <span key={index}>Target {index + 1}: {tp}</span>
                  ))
                ) : (
                  <span style={{ color: "var(--color-text-secondary)" }}>No targets specified</span>
                )}
              </div>
            </div>

            {recommendation.risk_reward_ratio !== undefined && recommendation.risk_reward_ratio !== null && (
              <div>
                <strong>Risk/Reward Ratio</strong>
                <p style={{ marginTop: "6px" }}>{recommendation.risk_reward_ratio.toFixed(2)}</p>
              </div>
            )}

            {/* ATLO Phase 0.1: Dynamic Entry Envelope Information */}
            {(recommendation.entry_min || recommendation.entry_max || recommendation.entry_optimal) && (
              <div style={{ 
                padding: "10px", 
                borderRadius: "6px", 
                background: "var(--color-info-light)", 
                border: "1px solid var(--color-info)" 
              }}>
                <strong style={{ color: "var(--color-info)" }}>Entry Envelope (ATLO)</strong>
                <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "3px" }}>
                  {recommendation.entry_optimal !== undefined && recommendation.entry_optimal !== null && (
                    <div>
                      <span style={{ fontWeight: "600" }}>Optimal Entry:</span> {recommendation.entry_optimal.toFixed(4)}
                    </div>
                  )}
                  {recommendation.entry_min !== undefined && recommendation.entry_min !== null && recommendation.entry_max !== undefined && recommendation.entry_max !== null && (
                    <div>
                      <span style={{ fontWeight: "600" }}>Envelope Range:</span> {recommendation.entry_min.toFixed(4)} - {recommendation.entry_max.toFixed(4)}
                      {recommendation.entry_optimal !== undefined && recommendation.entry_optimal !== null && recommendation.entry_optimal !== 0 && (
                        <span style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)", marginLeft: "8px" }}>
                          (Width: {((recommendation.entry_max - recommendation.entry_min) / recommendation.entry_optimal * 100).toFixed(2)}%)
                        </span>
                      )}
                    </div>
                  )}
                  {recommendation.slippage_tolerance_pct !== undefined && recommendation.slippage_tolerance_pct !== null && (
                    <div>
                      <span style={{ fontWeight: "600" }}>Slippage Tolerance:</span> {recommendation.slippage_tolerance_pct.toFixed(2)}%
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ATLO Refresh Information */}
            {(recommendation.refresh_count !== undefined && recommendation.refresh_count > 0) || recommendation.last_refresh_at || recommendation.envelope_valid_until ? (
              <div style={{ 
                padding: "10px", 
                borderRadius: "6px", 
                background: "var(--color-warning-light)", 
                border: "1px solid var(--color-warning)" 
              }}>
                <strong style={{ color: "var(--color-warning)" }}>Refresh Information (ATLO)</strong>
                <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "3px" }}>
                  {recommendation.refresh_count !== undefined && (
                    <div>
                      <span style={{ fontWeight: "600" }}>Refresh Count:</span> {recommendation.refresh_count}
                    </div>
                  )}
                  {recommendation.last_refresh_at && (
                    <div>
                      <span style={{ fontWeight: "600" }}>Last Refresh:</span> {formatDate(recommendation.last_refresh_at)}
                    </div>
                  )}
                  {recommendation.envelope_valid_until && (
                    <div>
                      <span style={{ fontWeight: "600" }}>Envelope Valid Until:</span> {formatDate(recommendation.envelope_valid_until)}
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* ATLO Orchestration State */}
            {recommendation.orchestration_state && (
              <div style={{ 
                padding: "10px", 
                borderRadius: "6px", 
                background: "var(--color-success-light)", 
                border: "1px solid var(--color-success)" 
              }}>
                <strong style={{ color: "var(--color-success)" }}>Orchestration State (ATLO)</strong>
                <p style={{ marginTop: "6px", textTransform: "capitalize" }}>
                  {recommendation.orchestration_state}
                </p>
              </div>
            )}

            {/* ATLO Risk/Reward Ratios */}
            {(recommendation.expected_rr_ratio !== undefined && recommendation.expected_rr_ratio !== null) || (recommendation.current_rr_ratio !== undefined && recommendation.current_rr_ratio !== null) ? (
              <div style={{ 
                padding: "10px", 
                borderRadius: "6px", 
                background: "var(--color-info-light)", 
                border: "1px solid var(--color-info)" 
              }}>
                <strong style={{ color: "var(--color-info)" }}>Risk/Reward Ratios (ATLO)</strong>
                <div style={{ marginTop: "6px", display: "flex", flexDirection: "column", gap: "3px" }}>
                  {recommendation.expected_rr_ratio !== undefined && recommendation.expected_rr_ratio !== null && (
                    <div>
                      <span style={{ fontWeight: "600" }}>Expected RR:</span> {recommendation.expected_rr_ratio.toFixed(2)}
                    </div>
                  )}
                  {recommendation.current_rr_ratio !== undefined && recommendation.current_rr_ratio !== null && (
                    <div>
                      <span style={{ fontWeight: "600" }}>Current RR:</span> {recommendation.current_rr_ratio.toFixed(2)}
                      {recommendation.expected_rr_ratio !== undefined && recommendation.expected_rr_ratio !== null && recommendation.expected_rr_ratio !== 0 && (
                        <span style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)", marginLeft: "8px" }}>
                          (Change: {((recommendation.current_rr_ratio - recommendation.expected_rr_ratio) / recommendation.expected_rr_ratio * 100).toFixed(1)}%)
                        </span>
                      )}
                    </div>
                  )}
                  {recommendation.goal_achievement_pct !== undefined && recommendation.goal_achievement_pct !== null && (
                    <div>
                      <span style={{ fontWeight: "600" }}>Goal Achievement:</span> {recommendation.goal_achievement_pct.toFixed(1)}%
                      <span style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)", marginLeft: "8px" }}>
                        ({recommendation.goal_achievement_pct >= 50 ? "Halfway+" : recommendation.goal_achievement_pct >= 25 ? "Quarter+" : "Early stage"})
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Expected Duration */}
            {recommendation.expected_duration_minutes && (
              <div>
                <strong>Expected Duration</strong>
                <p style={{ marginTop: "6px" }}>
                  {recommendation.expected_duration_minutes} minutes
                  {recommendation.expected_duration_minutes >= 60 && (
                    <span style={{ fontSize: "0.875rem", color: "var(--color-text-secondary)", marginLeft: "8px" }}>
                      ({Math.floor(recommendation.expected_duration_minutes / 60)}h {recommendation.expected_duration_minutes % 60}m)
                    </span>
                  )}
                </p>
              </div>
            )}

            <div>
              <strong>Generated At</strong>
              <p style={{ marginTop: "6px" }}>{formatDate(recommendation.generated_at)}</p>
            </div>

            {recommendation.expires_at && (
              <div>
                <strong>Expires At</strong>
                <p style={{ marginTop: "6px" }}>{formatDate(recommendation.expires_at)}</p>
              </div>
            )}

            <div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "16px" }}>
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}

